/**
 * One read per deal, for the two workflow tabs that need more than the deal
 * list carries: Needs review (each latest run's facts) and History (each
 * deal's run versions).
 *
 * Deals are few (a firm screens a handful at a time) and there is no
 * workflow-wide endpoint for either answer, so the reads fan out in parallel
 * — and only while `enabled`, i.e. while the tab that shows them is open.
 *
 * `load` must be a stable function (module-level or memoised): it is part of
 * the effect's identity. It returns `null` for a deal with nothing to read,
 * which drops that deal from `results` without costing a request. A deal whose
 * read fails is named in `error`; the others still show.
 */

import { useEffect, useMemo, useState } from "react";

import type { DealScreeningDeal } from "../types/dealScreening";

export interface PerDealResult<T> {
	deal: DealScreeningDeal;
	value: T;
}

export interface PerDealData<T> {
	results: PerDealResult<T>[];
	loading: boolean;
	error: string | null;
}

export function useDealScreeningPerDeal<T>(
	deals: DealScreeningDeal[],
	enabled: boolean,
	load: (deal: DealScreeningDeal) => Promise<T> | null,
): PerDealData<T> {
	const [results, setResults] = useState<PerDealResult<T>[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Re-read when a deal appears or its latest run changes (a new version,
	// a write that moved its counts) — not on every poll that changed nothing.
	const signature = useMemo(
		() =>
			deals
				.map(
					(deal) =>
						`${deal.public_id}:${deal.latest_run?.public_id ?? ""}:${
							deal.latest_run?.version ?? ""
						}:${deal.latest_run?.generating ? 1 : 0}:${
							deal.latest_run?.blocking_open ?? ""
						}:${deal.latest_run?.conflicts_open ?? ""}:${
							deal.latest_run?.gaps_open ?? ""
						}:${deal.latest_run?.status ?? ""}`,
				)
				.join("|"),
		[deals],
	);

	useEffect(() => {
		if (!enabled) return;
		let cancelled = false;
		const pending = deals
			.map((deal) => ({ deal, request: load(deal) }))
			.filter(
				(entry): entry is { deal: DealScreeningDeal; request: Promise<T> } =>
					entry.request !== null,
			);
		setLoading(true);
		void Promise.allSettled(pending.map((entry) => entry.request)).then(
			(settled) => {
				if (cancelled) return;
				const next: PerDealResult<T>[] = [];
				const failed: string[] = [];
				settled.forEach((outcome, index) => {
					const { deal } = pending[index];
					if (outcome.status === "fulfilled") {
						next.push({ deal, value: outcome.value });
					} else {
						failed.push(deal.name);
					}
				});
				setResults(next);
				setError(
					failed.length === 0 ? null : `Could not load ${failed.join(", ")}.`,
				);
				setLoading(false);
			},
		);
		return () => {
			cancelled = true;
		};
		// `deals` is read through `signature`, which changes exactly when a
		// re-read is owed.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [enabled, signature, load]);

	return { results, loading, error };
}
